import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { societyCreateGroupSchema } from '../../../forms/schemas/society_group_create'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { Modal } from '../../common/Modal'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import { theme } from '../../../theme/theme'
import { ModalBody } from '../../common/Modal/ModalBody'
import { CreateUpdateRegisterGroupForm } from './CreateUpdateRegisterGroupForm'
import { useWindowDimensions } from '../../../hooks/useWindowDimensions'
import { IGroupModel } from '../../../interfaces/models/groups.interfaces'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { createGroup, patchGroup } from '../../../api/groups'

interface CreateUpdateRegisterGroupModalProps {
  show: boolean
  close: () => void
  group?: IGroupModel
}

export const CreateUpdateRegisterGroupModal = ({
  show,
  close,
  group,
}: CreateUpdateRegisterGroupModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { height } = useWindowDimensions()
  const { setToastNotification } = useToastNotifications()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()

  const updateMode = !!group

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate(
        updateMode
          ? 'registerGroup.flashMessage.updateSuccess'
          : 'registerGroup.flashMessage.createSuccess'
      )
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate(
        updateMode
          ? 'registerGroup.flashMessage.updateFailure'
          : 'registerGroup.flashMessage.createFailure'
      )
    )
  }

  const { mutate: mutatePatchGroup, isPending: isPatchingGroup } = useMutation({
    mutationFn: patchGroup,
    onSuccess: () => {
      showSuccessMessage()
      close()
    },
    onError: () => {
      showErrorMessage()
    },
  })
  const { mutate: mutateCreateGroup, isPending: isCreatingGroup } = useMutation(
    {
      mutationFn: createGroup,
      onSuccess: () => {
        showSuccessMessage()
        close()
      },
      onError: () => {
        showErrorMessage()
      },
    }
  )

  const onSubmit = async (
    data: InferType<typeof societyCreateGroupSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchGroup({
        id: group._id,
        body: data,
      })
    } else {
      mutateCreateGroup(data)
    }
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isLoadingSociety || isCreatingGroup || isPatchingGroup

  return (
    <Modal show={show} height={height * 0.8} size="lg">
      {{
        header: (
          <ModalHeader onClose={close}>
            <div>
              <p style={theme.textVariants.h3}>
                {translate(
                  updateMode
                    ? 'registerGroup.modal.update'
                    : 'registerGroup.modal.create'
                )}
              </p>
            </div>
          </ModalHeader>
        ),
        body: (
          <ModalBody>
            {society && (
              <CreateUpdateRegisterGroupForm
                society={society}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                group={group}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
